// Client/src/Error/ErrorBoundary.tsx

import React, { Component, ReactNode, useState, useEffect, useCallback } from "react";
import html2canvas from "html2canvas";
import { useAuthStore } from "../store/authStore"; // Importiere den Auth-Store
import { Bug, Loader2, WifiOff } from "lucide-react";
import Portal from "../components/Portal";
import { httpClient } from "../core/SessionManager";
import { UiComponentSnapshotItem, getUiSnapshot } from "../utils/uiComponentTracker";
import { ErrorContext, useErrorContext, type ErrorContextType, type RequestDetails, type IssueType } from "./ErrorContext";

const isDevelopment = import.meta.env.MODE !== "production";

/*
  ---------------------------------------------------
  ErrorBoundary-Klasse
  ---------------------------------------------------
*/
interface ErrorBoundaryProps {
  children: ReactNode;
}

class ErrorBoundary extends Component<ErrorBoundaryProps> {
  declare context: React.ContextType<typeof ErrorContext>;
  static contextType = ErrorContext;

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.context.setError(error, errorInfo);
  }

  render(): ReactNode {
    return this.props.children;
  }
}

/*
  ---------------------------------------------------
  ErrorProvider
  ---------------------------------------------------
*/
export const ErrorProvider = ({ children }: { children: ReactNode }) => {
  const [errorState, _setErrorState] = useState<Error | null>(null);
  const [errorInfo, setErrorInfo] = useState<React.ErrorInfo | null>(null);
  const [requestDetails, setRequestDetails] = useState<RequestDetails | null>(null);
  const [serverErrorDetails, setServerErrorDetails] = useState<unknown | null>(null);
  const [uiSnapshot, setUiSnapshot] = useState<UiComponentSnapshotItem[]>([]);
  const [issueType, setIssueType] = useState<IssueType>('BUG');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [description, setDescription] = useState("");
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [retryCountdown, setRetryCountdown] = useState(0);
  const [isSendingInBackground, setIsSendingInBackground] = useState(false);
  const [hasSendError, setHasSendError] = useState(false);

  // Zugriff auf den Auth-Store
  const user = useAuthStore((state) => state.user);
  const selectedStoreId = useAuthStore((state) => state.selectedStoreId);
  const stores = useAuthStore((state) => state.stores);
  const selectedStore = stores.find((s) => s.id === selectedStoreId) || null;

  const setError = (err: Error | null, info?: React.ErrorInfo) => {
    console.log("DEBUG: Context setError aufgerufen mit =>", err, info);
    _setErrorState(err);
    if (info) {
      setErrorInfo(info);
      setServerErrorDetails(null);
    }
    if (err || info) {
      setIssueType('BUG');
    }
    setUiSnapshot(getUiSnapshot());
  };

  const captureScreenshot = useCallback(async (): Promise<string | null> => {
    try {
      const canvas = await html2canvas(document.body);
      const imgData = canvas.toDataURL("image/png");
      setScreenshot(imgData);
      console.log("DEBUG: Screenshot erfolgreich erfasst");
      return imgData;
    } catch (err) {
      console.error("Screenshot capture failed:", err);
      return null;
    }
  }, []);

  const openModal = useCallback(
    (forceScreenshot?: boolean) => {
      console.log("DEBUG: openModal() => forceScreenshot=", forceScreenshot);
      if (!errorState && !errorInfo && !requestDetails) {
        setUiSnapshot(getUiSnapshot());
      }
      setIssueType('BUG');
      // Screenshot wird nicht mehr automatisch erstellt
      setIsModalOpen(true);
    },
    [errorState, errorInfo, requestDetails]
  );

  const closeModal = useCallback(() => {
    setError(null);
    setErrorInfo(null);
    setRequestDetails(null);
    setServerErrorDetails(null);
    setScreenshot(null);
    setDescription(""); // Beschreibung nach Schließen oder Senden löschen
    setUiSnapshot([]);
    setIssueType('BUG');
    setIsModalOpen(false);
    // Reload nach Schließen verhindern - nur State zurücksetzen
  }, []);

  const sendErrorReport = useCallback(async (withScreenshot: boolean, screenshotOverride?: string | null) => {
    setIsSendingInBackground(true);
    setHasSendError(false);
    
    try {
      const screenshotPayload = withScreenshot ? (screenshotOverride ?? screenshot) : null;
      
      const payload = {
        error: errorState?.message || undefined,
        componentStack: errorInfo?.componentStack || undefined,
        requestDetails: requestDetails || undefined,
        uiSnapshot: uiSnapshot.length > 0 ? uiSnapshot : undefined,
        screenshot: screenshotPayload,
        description,
        issueType,
        user: user
          ? {
              id: user.id,
              employee_id: user.employee_id,
              first_name: user.first_name,
              last_name: user.last_name,
              role: user.role,
            }
          : null,
        selectedStore: selectedStore
          ? {
              id: selectedStore.id,
              storeId: selectedStore.store_id,
              name: selectedStore.name,
              location: selectedStore.location,
            }
          : null,
      };
      
      const response = await httpClient.post<{ success?: boolean; message?: string; error?: string }, typeof payload>(
        "/api/issues",
        payload
      );

      if (response && response.success === false) {
        throw new Error(response.error || "Fehler beim Senden des Berichts");
      }
      
      console.log("DEBUG: Fehlerbericht erfolgreich gesendet");
      setHasSendError(false);
      
      // Erfolgreich gesendet - Modal schließen und State zurücksetzen
      setError(null);
      setErrorInfo(null);
      setRequestDetails(null);
      setScreenshot(null);
      setDescription("");
      setIsModalOpen(false);
      
    } catch (sendError) {
      console.error("Fehler beim Senden des Fehlerberichts:", sendError);
      // Setze Fehler-Flag, aber öffne NICHT erneut den ErrorBoundary
      setHasSendError(true);
    } finally {
      setIsSendingInBackground(false);
    }
  }, [errorState, errorInfo, requestDetails, uiSnapshot, screenshot, description, issueType, user, selectedStore]);

  useEffect(() => {
    if (errorState) {
      queueMicrotask(() => {
        openModal();
      });
    }
  }, [errorState, openModal]);

  // Offline-Detection
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setRetryCountdown(0);
      // Schließe Modal, wenn online
      closeModal();
    };

    const handleOffline = () => {
      setIsOffline(true);
      setRetryCountdown(60); // Starte Countdown bei 60s
      // Öffne Modal mit Offline-Details
      setRequestDetails({
        url: 'Offline',
        method: 'GET',
        body: null,
        isOffline: true
      });
      openModal();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    if (!navigator.onLine) {
      handleOffline();
    }

    // Countdown-Timer für Retry
    const countdownInterval = setInterval(() => {
      if (isOffline && retryCountdown > 0) {
        setRetryCountdown(prev => prev - 1);
      } else if (isOffline && retryCountdown === 0) {
        // Versuche Reconnect nach 60s
        if (navigator.onLine) {
          handleOnline();
        } else {
          setRetryCountdown(60); // Reset Countdown
        }
      }
    }, 1000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(countdownInterval);
    };
  }, [isOffline, retryCountdown, openModal, closeModal]);

  const contextValue: ErrorContextType = {
    error: errorState,
    errorInfo,
    requestDetails,
    serverErrorDetails,
    uiSnapshot,
    issueType,
    setIssueType,
    setError,
    setRequestDetails,
    setServerErrorDetails,
    isModalOpen,
    openModal,
    closeModal,
    screenshot,
    captureScreenshot,
    description,
    setDescription,
    isOffline,
    retryCountdown,
    isSendingInBackground,
    hasSendError,
    sendErrorReport,
  };

  return (
    <ErrorContext.Provider value={contextValue}>
      <ErrorBoundary>
        {children}

        {isModalOpen && <ErrorModalBox />}

        <BugReportButton />
      </ErrorBoundary>
    </ErrorContext.Provider>
  );
};


/*
  ---------------------------------------------------
  BugReportButton - Floating Button mit Status-Anzeige
  ---------------------------------------------------
*/
function BugReportButton() {
  const { openModal, isSendingInBackground, hasSendError } = useErrorContext();
  const user = useAuthStore((state) => state.user);
  const isCleaningStaff = user?.role === "reinigungskraft";

  // Button nur anzeigen, wenn Benutzer eingeloggt ist und keine Reinigungskraft ist
  if (!user || isCleaningStaff) {
    return null;
  }
  
  // Button-Farbe basierend auf Status
  const buttonColor = hasSendError 
    ? "bg-red-600 hover:bg-red-700" 
    : "bg-blue-600 hover:bg-blue-700";
  
  const handleClick = () => {
    // Öffne das Modal (der Fehler-Status wird beim erfolgreichen Senden zurückgesetzt)
    openModal();
  };
  
  return (
    <button
      onClick={handleClick}
      className={`fixed bottom-6 right-6 w-14 h-14 rounded-full text-white
                 flex items-center justify-center shadow-lg transition-colors duration-300 ${buttonColor}`}
      style={{ zIndex: 9999 }}
      aria-label="Issue melden"
      title={hasSendError ? "Fehler beim letzten Senden - Erneut versuchen" : "Issue melden"}
    >
      {isSendingInBackground ? (
        <Loader2 className="animate-spin" size={24} />
      ) : (
        <Bug size={24} />
      )}
    </button>
  );
}

/*
  ---------------------------------------------------
  ErrorModalBox
  ---------------------------------------------------
*/
function ErrorModalBox() {
  const {
    error,
    errorInfo,
    requestDetails,
    serverErrorDetails,
    uiSnapshot,
    issueType,
    setIssueType,
    screenshot,
    description,
    setDescription,
    closeModal,
    setError,
    retryCountdown,
    captureScreenshot,
    sendErrorReport,
  } = useErrorContext();

  // Zugriff auf den Auth-Store
  const user = useAuthStore((state) => state.user);
  const selectedStoreId = useAuthStore((state) => state.selectedStoreId);
  const stores = useAuthStore((state) => state.stores);
  const selectedStore = stores.find((s) => s.id === selectedStoreId) || null;

  const [isCapturingScreenshot, setIsCapturingScreenshot] = useState(false);
  const [issueNumberInput, setIssueNumberInput] = useState("");
  const [issueValidation, setIssueValidation] = useState<{
    status: 'idle' | 'checking' | 'valid' | 'invalid';
    title?: string;
    url?: string;
    message?: string;
  }>({ status: 'idle' });
  
  // Prüfe, ob es sich um einen Offline-Fehler handelt
  const isOfflineError = requestDetails?.isOffline;
  const isIssueCommentMode =
    isDevelopment && issueValidation.status === 'valid' && issueNumberInput.trim() !== "";
  const uiSnapshotSections = uiSnapshot.reduce<Record<string, UiComponentSnapshotItem[]>>(
    (acc, entry) => {
      const key = entry.kind || "component";
      if (!acc[key]) acc[key] = [];
      acc[key].push(entry);
      return acc;
    },
    {}
  );
  const uiKindLabels: Record<string, string> = {
    page: "Seite",
    modal: "Modal",
    overlay: "Overlay",
    component: "Komponente",
  };
  const issueTypeOptions: Array<{ value: IssueType; label: string }> = [
    { value: "BUG", label: "Fehler" },
    { value: "Anfrage", label: "Anfrage" },
    { value: "Feature", label: "Feature" },
  ];

  useEffect(() => {
    if (!isDevelopment) {
      return;
    }

    const trimmed = issueNumberInput.trim();
    if (!trimmed) {
      queueMicrotask(() => {
        setIssueValidation({ status: 'idle' });
      });
      return;
    }

    if (!/^\d+$/.test(trimmed)) {
      queueMicrotask(() => {
        setIssueValidation({ status: 'invalid', message: 'Nur Zahlen erlaubt.' });
      });
      return;
    }

    let isActive = true;
    queueMicrotask(() => {
      setIssueValidation({ status: 'checking' });
    });

    const timeout = window.setTimeout(async () => {
      try {
        const response = await httpClient.get<{ valid: boolean; title?: string; url?: string }>(
          `/api/issues/${trimmed}/validate`
        );
        if (!isActive) {
          return;
        }
        if (response.valid) {
          setIssueValidation({
            status: 'valid',
            title: response.title,
            url: response.url,
          });
        } else {
          setIssueValidation({
            status: 'invalid',
            message: 'Issue nicht gefunden oder kein Zugriff.',
          });
        }
      } catch {
        if (!isActive) {
          return;
        }
        setIssueValidation({
          status: 'invalid',
          message: 'Issue nicht gefunden oder kein Zugriff.',
        });
      }
    }, 300);

    return () => {
      isActive = false;
      window.clearTimeout(timeout);
    };
  }, [issueNumberInput]);

  const handleIgnore = () => {
    setError(null);
    closeModal();
  };

  const handleSendIssueComment = async () => {
    const trimmed = issueNumberInput.trim();
    if (!trimmed || issueValidation.status !== 'valid') {
      alert("Bitte eine gültige Issue-Nummer angeben.");
      return;
    }

    if (isOfflineError || !navigator.onLine) {
      alert("Sie sind offline. Der Fehlerbericht kann nicht gesendet werden.");
      return;
    }

    const payload = {
      error_message: error?.message || "Unbekannter Fehler",
      stack_trace: errorInfo?.componentStack || error?.stack || "",
      user_context: {
        description,
        requestDetails,
        uiSnapshot,
        server_error_details: serverErrorDetails,
        user: user
          ? {
              id: user.id,
              employee_id: user.employee_id,
              first_name: user.first_name,
              last_name: user.last_name,
              role: user.role,
            }
          : null,
        selectedStore: selectedStore
          ? {
              id: selectedStore.id,
              storeId: selectedStore.store_id,
              name: selectedStore.name,
              location: selectedStore.location,
            }
          : null,
      },
      timestamp: new Date().toISOString(),
    };

    closeModal();
    try {
      await httpClient.post(`/api/issues/${trimmed}/error-comment`, payload);
    } catch (sendError) {
      console.error("Fehler beim Senden des Fehlerkommentars:", sendError);
      alert("Fehler beim Senden des Fehlerkommentars.");
    }
  };

  const handleSendWithScreenshot = async () => {
    if (isIssueCommentMode) {
      await handleSendIssueComment();
      return;
    }

    if (isOfflineError || !navigator.onLine) {
      alert("Sie sind offline. Der Fehlerbericht kann nicht gesendet werden.");
      return;
    }
    
    setIsCapturingScreenshot(true);
    // Modal sofort schließen und im Hintergrund arbeiten
    closeModal();
    
    // Screenshot erstellen
    setTimeout(async () => {
      let captured: string | null = null;
      try {
        captured = await captureScreenshot();
      } catch (err) {
        console.error("Screenshot konnte nicht erstellt werden:", err);
      } finally {
        setIsCapturingScreenshot(false);
        await sendErrorReport(true, captured);
      }
    }, 0);
  };

  const handleSendWithoutScreenshot = async () => {
    if (isIssueCommentMode) {
      await handleSendIssueComment();
      return;
    }

    if (isOfflineError || !navigator.onLine) {
      alert("Sie sind offline. Der Fehlerbericht kann nicht gesendet werden.");
      return;
    }
    
    // Modal sofort schließen und im Hintergrund senden
    closeModal();
    await sendErrorReport(false);
  };

  return (
        <Portal track={false}>
      <div className="fixed inset-0 z-[30000] flex items-start justify-center bg-slate-900/75 p-2 pt-6 sm:p-4 sm:pt-12 overflow-y-auto">
        <div className="relative flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl my-8 p-6">
        <h2 className="text-xl font-bold mb-2 text-red-600">
          {isOfflineError ? "Verbindungsproblem" : "Fehler oder Issue melden"}
        </h2>

        {isOfflineError && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <div className="flex items-center">
              <WifiOff className="text-yellow-500 mr-2" size={20} />
              <p className="text-yellow-700">
                Keine Internetverbindung. Die Anwendung pausiert automatisch und versucht in {retryCountdown} Sekunden erneut zu verbinden.
              </p>
            </div>
            <div className="flex items-center justify-center mt-2">
              <Loader2 className="animate-spin text-blue-600 mr-2" size={16} />
              <span className="text-sm text-yellow-600">Warte auf Verbindung...</span>
            </div>
            <p className="text-sm text-yellow-600 mt-2">
              Bitte überprüfen Sie Ihre Netzwerkverbindung. Die App wird automatisch fortfahren, sobald die Verbindung wiederhergestellt ist.
            </p>
          </div>
        )}

        {error && !isOfflineError && (
          <div className="text-sm text-gray-700 mb-4">
            <strong>Fehlermeldung:</strong> {error?.message || 'Unbekannter Fehler'}
          </div>
        )}

        {errorInfo && !isOfflineError && (
          <div className="text-sm text-gray-700 mb-4">
            <strong>Komponente:</strong> {errorInfo?.componentStack || 'Nicht verfügbar'}
          </div>
        )}

        {isDevelopment && serverErrorDetails !== null && !isOfflineError && (
          <div className="text-sm text-gray-700 mb-4">
            <strong>Server-Details:</strong>
            <pre className="mt-2 max-h-40 overflow-auto rounded bg-slate-100 p-2 text-xs text-slate-700">
              {JSON.stringify(serverErrorDetails, null, 2)}
            </pre>
          </div>
        )}

        {!isOfflineError && uiSnapshot.length > 0 && (
          <div className="text-sm text-gray-700 mb-4">
            <strong>UI-Kontext:</strong>
            {Object.entries(uiSnapshotSections).map(([kind, entries]) => {
              const label = uiKindLabels[kind] || kind;
              const formatted = entries
                .map((entry) => (entry.count > 1 ? `${entry.name} (x${entry.count})` : entry.name))
                .join(', ');
              return (
                <div key={kind}>
                  <span className="font-semibold">{label}:</span> {formatted}
                </div>
              );
            })}
          </div>
        )}

        {!isOfflineError && (
          <div className="text-sm text-gray-700 mb-4">
            <label className="block font-semibold mb-1" htmlFor="issueTypeSelect">
              Anfragen-Typ:
            </label>
            <select
              id="issueTypeSelect"
              value={issueType}
              onChange={(event) => setIssueType(event.target.value as IssueType)}
              className="w-full border border-gray-300 rounded p-2"
            >
              {issueTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}

        {user && !isOfflineError && (
          <div className="text-sm text-gray-700 mb-4">
            <strong>Benutzerinformationen:</strong>
            <br />
            Name: {user?.first_name || 'N/A'} {user?.last_name || ''}
            <br />
          </div>
        )}

        {selectedStore && !isOfflineError && (
          <div className="text-sm text-gray-700 mb-4">
            <strong>Ausgewählter Markt:</strong>
            <br />
            Store ID: {selectedStore?.store_id || 'N/A'}
            <br />
            Name: {selectedStore?.name || 'N/A'}
            <br />
          </div>
        )}

        {screenshot && !isOfflineError && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 mb-2">Screenshot-Vorschau:</p>
            {/* Hier die Größenbeschränkung für das Bild */}
            <img
              src={screenshot || undefined}
              alt="Screenshot"
              className="border max-w-[25vw] max-h-[calc(var(--app-window-height)*0.25)] object-contain"
            />
          </div>
        )}

        {!isOfflineError && (
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Hier eine kurze Beschreibung eintragen..."
            className="w-full border border-gray-300 rounded p-2 mb-4"
            rows={3}
          />
        )}

        {!isOfflineError && isDevelopment && (
          <div className="text-sm text-gray-700 mb-4">
            <label className="block font-semibold mb-1" htmlFor="issueNumberInput">
              Issue-Nummer (Dev):
            </label>
            <input
              id="issueNumberInput"
              type="text"
              inputMode="numeric"
              value={issueNumberInput}
              onChange={(event) => setIssueNumberInput(event.target.value)}
              placeholder="z.B. 1234"
              className="w-full border border-gray-300 rounded p-2"
            />
            {issueValidation.status === 'checking' && (
              <div className="text-xs text-gray-500 mt-1">Prüfe Issue...</div>
            )}
            {issueValidation.status === 'valid' && (
              <div className="text-xs text-green-600 mt-1">
                Issue gefunden: {issueValidation.title || 'Ohne Titel'}
                {issueValidation.url ? ` (${issueValidation.url})` : ''}
              </div>
            )}
            {issueValidation.status === 'invalid' && (
              <div className="text-xs text-red-600 mt-1">
                {issueValidation.message || 'Issue ungültig.'}
              </div>
            )}
            {isIssueCommentMode && (
              <div className="text-xs text-blue-600 mt-1">
                Kommentar wird an bestehendes Issue angehängt.
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          {isOfflineError ? (
            <p className="text-sm text-gray-500">
              Die Modal schließt sich automatisch, sobald die Verbindung wiederhergestellt ist.
            </p>
          ) : (
            <>
              <button
                onClick={handleIgnore}
                className="bg-gray-300 text-gray-800 py-2 px-4 rounded hover:bg-gray-400"
              >
                Schließen
              </button>
              <button
                onClick={handleSendWithoutScreenshot}
                disabled={!navigator.onLine}
                className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isIssueCommentMode ? "Kommentar senden" : "Senden"}
              </button>
              <button
                onClick={handleSendWithScreenshot}
                disabled={isIssueCommentMode || isCapturingScreenshot || !navigator.onLine}
                className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCapturingScreenshot ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    Screenshot...
                  </>
                ) : (
                  "Mit Screenshot senden"
                )}
              </button>
            </>
          )}
        </div>
      </div>
      </div>
    </Portal>
  );
}
